import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { slides } from './deckSlides';
import { useScreenInit } from './useScreenInit';

// Host <-> deck sync messages. Do NOT change these strings.
const MP_SLIDE_CHANGED = 'MP_SLIDE_CHANGED';
const MP_NAVIGATE_SLIDE = 'MP_NAVIGATE_SLIDE';

/**
 * Deck harness — DO NOT EDIT. Owns the single `slideIndex` navigation state,
 * keyboard + host-driven navigation, and the host-sync postMessage, and renders
 * exactly one full-bleed slide at a time. Build slides in deckSlides.tsx (in
 * presentation order) and keep canvas.manifest.js in sync — never reimplement
 * navigation or host-sync here or inside a slide.
 */
export function App() {
  const total = slides.length;
  const seeded = (useScreenInit() as {
    slideIndex?: number;
  } | undefined)?.slideIndex ?? 0;
  const [slideIndex, setSlideIndex] = useState(() => clampIndex(seeded, total));

  // Keyboard navigation (preview + present mode).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === ' ' || event.key === 'PageDown') {
        event.preventDefault();
        setSlideIndex(prev => clampIndex(prev + 1, total));
      } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        setSlideIndex(prev => clampIndex(prev - 1, total));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [total]);

  // Host-driven navigation (e.g. present mode) via postMessage into the iframe.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== MP_NAVIGATE_SLIDE) return;
      const delta = event.data.direction === 'prev' ? -1 : 1;
      setSlideIndex(prev => clampIndex(prev + delta, total));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [total]);

  // Mirror every real slide change to the host (skip the initial render) so the
  // editor filmstrip / present mode stay in lockstep with in-deck navigation.
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    window.parent.postMessage({
      type: MP_SLIDE_CHANGED,
      slideIndex
    }, '*');
  }, [slideIndex]);
  const current = slides[clampIndex(slideIndex, total)];
  const SlideComponent = current?.component;
  return <div className="relative h-screen w-screen overflow-hidden bg-black" data-id="element-0" data-mp="3bkgq21u6m1bk">
      <AnimatePresence mode="wait" data-id="element-1" data-mp="3qcr2tdsdjzms">
        <motion.div key={slideIndex} className="absolute inset-0" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4,
        ease: 'easeInOut'
      }} data-id="element-2" data-mp="3lkhfm5in9do7">
          {SlideComponent ? <SlideComponent data-id="element-3" data-mp="2stpkzch88x2z" /> : null}
        </motion.div>
      </AnimatePresence>
    </div>;
}
function clampIndex(index: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(total - 1, Math.max(0, index));
}